import {take, takeUntil, type Observable} from "rxjs";

import {createBeeper, type Beeper} from "../helpers/beep.helper";
import {endsAt, finish, isRunning, parseStored, remaining, type TimerState} from "../domain/gimmicks/timer.state";
import {isTimerKey, listTimerKeys, readMutedAt, readStateAt, writeStateAt} from "../domain/gimmicks/timer.store";

/**
 * The app-wide timer watcher (feature plan 0073, Anke's decision 5: the tone comes in any view of the app, as
 * long as the page is open). It knows no elements and no tiles — only the stored timer runs, by their finished
 * keys (`timer.store.ts`). Elements subscribe per key (`watchTimer`); `ha-app` binds it to the session.
 *
 * - Start: reads every stored run. A running one gets ONE `setTimeout` to its end (no interval, no chain). One
 *   whose end lies more than 5 s back ended while the page was closed: finished silently.
 * - Ending (timer fires, or `check` from an element), synchronous: read the key afresh; only a run that is
 *   running and has nothing left is finished — written, reported to this tab's subscribers, then the tone
 *   (unless the tile is muted). A run that still has time (a timer firing early against the wall clock) is
 *   re-armed. A key gone, invalid or not running is dropped, nothing written. A reset or "+" from another tab
 *   is thus never overwritten.
 * - Once per run: the `runningSince` of every run finished here is remembered; the same run is never finished or
 *   beeped twice in this tab (also when writing failed and the run still "runs").
 * - Other tabs: an own `storage` listener re-arms or drops timers; it writes nothing but the end of a run whose time
 *   is up (as every path here does — see "Ending"). A FRESH end written by another
 *   tab (the old value ran, the new one is its end, at most 2 s ago — or this tab still had a timer for that very
 *   run) beeps here too: a never-clicked tab (a beamer window) often sees the end first and finishes silently.
 *   Several open tabs may thus beep more than once (decision 6). A cleared storage drops everything and reads anew.
 * - Sound: one shared `Beeper`. A gesture listener on `document` primes it while a timer runs and the beeper does
 *   not report `running()`; once the resume has settled, the next re-evaluation (a gesture, `changed`, an end)
 *   removes it. With no timer and no tone pending, the context is suspended and the listener removed.
 */
export interface TimerWatchSession {
    readonly isUserLoggedIn: boolean;
    listen$(key: 'user'): Observable<unknown>;
}

interface Config {
    beeper: () => Beeper;
    now: () => number;
    schedule: (callback: () => void, ms: number) => unknown;
    cancel: (handle: unknown) => void;
}

const SILENT_AFTER_MS = 5000;
const FRESH_FOREIGN_END_MS = 2000;
const GESTURES = ['click', 'pointerup', 'keydown'] as const;

let sharedBeeper: Beeper | null = null;
const defaults: Config = {
    beeper: () => (sharedBeeper ??= createBeeper()),
    now: () => Date.now(),
    schedule: (callback, ms) => setTimeout(callback, ms),
    cancel: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
};
let config: Config = {...defaults};

let started = false;
let gesturesAttached = false;
let pendingTones = 0;
const timers = new Map<string, {handle: unknown; runningSince: number}>();
const finishedRuns = new Map<string, number>();
const subscribers = new Map<string, Set<(silent: boolean) => void>>();

/** For tests: the beeper, the clock and the timer functions. Resolved lazily, so fake timers apply. */
export function configureTimerWatch(overrides: Partial<Config>): void {
    config = {...defaults, ...overrides};
}

export function startTimerWatch(): void {
    if (started) {
        return;
    }
    started = true;
    if (typeof window !== 'undefined') {
        window.addEventListener('storage', onStorage);
    }
    scan();
}

export function stopTimerWatch(): void {
    if (!started) {
        return;
    }
    started = false;
    if (typeof window !== 'undefined') {
        window.removeEventListener('storage', onStorage);
    }
    for (const key of [...timers.keys()]) {
        disarm(key);
    }
    detachGestures();
    // A tone that is playing right now is not cut off: its `finally` in playTone re-evaluates and, with no timer
    // left, suspends then (review 0073-03).
    if (pendingTones === 0) {
        config.beeper().suspend();
    }
}

/**
 * Runs the watcher while somebody is logged in. Checks at once — after a reload with a valid session no `user`
 * event comes — then follows `listen$('user')`, always reading `isUserLoggedIn`, never the emitted value (the
 * subject replays its history). Ends with `until$`.
 */
export function bindTimerWatchToSession(session: TimerWatchSession, until$: Observable<unknown>): void {
    const apply = (): void => {
        if (session.isUserLoggedIn) {
            startTimerWatch();
        } else {
            stopTimerWatch();
        }
    };
    apply();
    session.listen$('user').pipe(takeUntil(until$)).subscribe(apply);
    until$.pipe(take(1)).subscribe(() => stopTimerWatch());
}

/** An element's subscription to the end of its run; `silent` = finished on start, without tone and announcement. */
export function watchTimer(key: string, onDone: (silent: boolean) => void): () => void {
    let set = subscribers.get(key);
    if (set === undefined) {
        set = new Set();
        subscribers.set(key, set);
    }
    set.add(onDone);
    return () => {
        set!.delete(onDone);
        if (set!.size === 0) {
            subscribers.delete(key);
        }
    };
}

/** Unlocks the shared tone — call inside a gesture (a click on a timer button). The audio state is re-evaluated
 *  only after the click handler, in a microtask like `onGesture`: at the moment of the call the click's run is not
 *  armed yet, and "no timer" would suspend the context just unlocked (live probe on stage, review 0073-02). The
 *  microtask covers the clicks that end without `changed` too (mute, a blocked ±; review 0073-03). */
export function prime(): void {
    config.beeper().prime();
    queueMicrotask(updateAudio);
}

/** Ends the run now if its time is up (also without a started watcher). Whether it was ended by this call. */
export function check(key: string): boolean {
    return tryFinish(key);
}

/** After a click in the element: re-arm or drop this run's timer. Writes nothing but the end of a run whose time is
 *  already up. */
export function changed(key: string): void {
    const state = readStateAt(key, config.now());
    if (state !== null && isRunning(state) && remaining(state, config.now()) === 0) {
        tryFinish(key);
        return;
    }
    rearm(key, state);
    updateAudio();
}

function scan(): void {
    const now = config.now();
    for (const key of listTimerKeys()) {
        const state = readStateAt(key, now);
        if (state === null || !isRunning(state)) {
            continue;
        }
        if (remaining(state, now) > 0) {
            arm(key, state);
        } else if (now - (endsAt(state) ?? now) > SILENT_AFTER_MS) {
            finishSilently(key, state);
        } else {
            tryFinish(key);
        }
    }
    updateAudio();
}

function arm(key: string, state: TimerState): void {
    disarm(key);
    if (!started || state.runningSince === null) {
        return;
    }
    const handle = config.schedule(() => {
        timers.delete(key);
        tryFinish(key);
        updateAudio();
    }, remaining(state, config.now()));
    timers.set(key, {handle, runningSince: state.runningSince});
}

function disarm(key: string): void {
    const timer = timers.get(key);
    if (timer !== undefined) {
        config.cancel(timer.handle);
        timers.delete(key);
    }
}

function rearm(key: string, state: TimerState | null): void {
    if (state !== null && isRunning(state) && remaining(state, config.now()) > 0) {
        arm(key, state);
    } else {
        disarm(key);
    }
}

function tryFinish(key: string): boolean {
    const now = config.now();
    const state = readStateAt(key, now);
    if (state === null || !isRunning(state)) {
        disarm(key);
        return false;
    }
    if (finishedRuns.get(key) === state.runningSince) {
        return false;
    }
    if (remaining(state, now) > 0) {
        arm(key, state);
        return false;
    }
    finishedRuns.set(key, state.runningSince!);
    disarm(key);
    writeStateAt(key, finish(state));
    notify(key, false);
    if (!readMutedAt(key)) {
        playTone();
    }
    updateAudio();
    return true;
}

function finishSilently(key: string, state: TimerState): void {
    finishedRuns.set(key, state.runningSince!);
    writeStateAt(key, finish(state));
    notify(key, true);
}

/** Reports an end to this tab's subscribers. One failing subscriber must not cost the others — nor the tone,
 *  which follows the report and could not come later (the run is remembered as finished). */
function notify(key: string, silent: boolean): void {
    for (const onDone of [...(subscribers.get(key) ?? [])]) {
        try {
            onDone(silent);
        } catch (error) {
            console.warn('A timer subscriber failed', error);
        }
    }
}

function onStorage(event: StorageEvent): void {
    if (event.storageArea !== undefined && event.storageArea !== null && event.storageArea !== localStorage) {
        return;
    }
    if (event.key === null) {
        for (const key of [...timers.keys()]) {
            disarm(key);
        }
        scan();
        return;
    }
    if (!isTimerKey(event.key)) {
        return;
    }
    const key = event.key;
    const now = config.now();
    // First the tone for a fresh foreign end — before re-evaluating the timers, or dropping the last one would
    // suspend the context right before the tone.
    const before = parse(event.oldValue, now);
    const after = parse(event.newValue, now);
    if (before !== null && before.runningSince !== null && after !== null && isEndOf(after, before)
        && finishedRuns.get(key) !== before.runningSince) {
        const ownTimer = timers.get(key)?.runningSince === before.runningSince;
        if (ownTimer || now - (endsAt(before) ?? now) <= FRESH_FOREIGN_END_MS) {
            finishedRuns.set(key, before.runningSince);
            if (!readMutedAt(key)) {
                playTone();
            }
        }
    }
    rearm(key, readStateAt(key, now));
    updateAudio();
}

function parse(raw: string | null, now: number): TimerState | null {
    if (raw === null) {
        return null;
    }
    try {
        return parseStored(JSON.parse(raw), now);
    } catch {
        // A broken old or new value of another tab's write is "no valid state" — on purpose without a warning: the
        // handler then reads the key itself (`readStateAt`), which warns about a broken current value.
        return null;
    }
}

function isEndOf(after: TimerState, before: TimerState): boolean {
    const end = finish(before);
    return after.runningSince === null && after.elapsedMs === end.elapsedMs && after.durationMs === end.durationMs;
}

function playTone(): void {
    pendingTones++;
    void config.beeper().beep().finally(() => {
        pendingTones--;
        updateAudio();
    });
}

function updateAudio(): void {
    const beeper = config.beeper();
    if (timers.size === 0 && pendingTones === 0) {
        detachGestures();
        beeper.suspend();
    } else if (timers.size > 0 && !beeper.running()) {
        attachGestures();
    } else {
        detachGestures();
    }
}

const onGesture = (): void => {
    config.beeper().prime();
    queueMicrotask(updateAudio);
};

function attachGestures(): void {
    if (gesturesAttached || typeof document === 'undefined') {
        return;
    }
    gesturesAttached = true;
    for (const type of GESTURES) {
        document.addEventListener(type, onGesture, {capture: true});
    }
}

function detachGestures(): void {
    if (!gesturesAttached || typeof document === 'undefined') {
        return;
    }
    gesturesAttached = false;
    for (const type of GESTURES) {
        document.removeEventListener(type, onGesture, {capture: true});
    }
}
